/**
 * Data hooks for the Monitoring admin feature.
 *
 * All five endpoints under `/api/v1/admin/monitoring/*` are bound here
 * with a 30 second `refetchInterval` so the page behaves like a live
 * dashboard. The OpenAPI generator types most of the responses as
 * `Record<string, never>` (Java side returns ad-hoc maps). We narrow
 * to local `interface`s on the typescript side and `as never`-cast at
 * the openapi-fetch boundary, with a TODO for backend schema work.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';

const REFETCH_INTERVAL_MS = 30_000;

export interface HealthComponent {
  status?: string;
  details?: Record<string, unknown>;
}

export interface HealthResponse {
  status?: string;
  components?: Record<string, HealthComponent>;
}

export interface MetricsResponse {
  uptimeSeconds?: number;
  jvm?: {
    heapUsed?: number;
    heapMax?: number;
    nonHeapUsed?: number;
    threads?: number;
    threadsPeak?: number;
    gcCount?: number;
  };
  os?: {
    cpuLoad?: number;
    availableProcessors?: number;
  };
  disk?: {
    free?: number;
    total?: number;
  };
  [key: string]: unknown;
}

export interface QueueRow {
  name?: string;
  isDlq?: boolean;
  messages?: number;
  consumers?: number;
}

export interface RequestLogEntry {
  id?: number;
  timestamp?: string;
  method?: string;
  uri?: string;
  status?: number;
  durationMs?: number;
  correlationId?: string;
  userId?: string | null;
  remoteAddr?: string | null;
}

export interface RequestLogDetail extends RequestLogEntry {
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: string;
  responseBody?: string;
  errorMessage?: string | null;
}

export const monitoringKeys = {
  all: ['monitoring'] as const,
  health: () => ['monitoring', 'health'] as const,
  metrics: () => ['monitoring', 'metrics'] as const,
  queues: () => ['monitoring', 'queues'] as const,
  requests: (limit: number) => ['monitoring', 'requests', limit] as const,
  requestDetail: (id: number) => ['monitoring', 'requests', 'detail', id] as const,
};

const buildOptions = <T,>(
  queryKey: readonly unknown[],
  fetcher: () => Promise<T>,
): {
  queryKey: readonly unknown[];
  queryFn: () => Promise<T>;
  refetchInterval: number;
} => ({
  queryKey,
  queryFn: fetcher,
  refetchInterval: REFETCH_INTERVAL_MS,
});

export function useHealth(): UseQueryResult<HealthResponse, Error> {
  return useQuery<HealthResponse, Error>(
    buildOptions(monitoringKeys.health(), async () => {
      const { data, error } = await api.GET('/api/v1/admin/monitoring/health');
      if (error) throw error as Error;
      // TODO: regenerate types — backend returns a structured map
      // (`{ status, components: { db, redis, ... } }`).
      return data as never as HealthResponse;
    }),
  );
}

export function useMetrics(): UseQueryResult<MetricsResponse, Error> {
  return useQuery<MetricsResponse, Error>(
    buildOptions(monitoringKeys.metrics(), async () => {
      const { data, error } = await api.GET('/api/v1/admin/monitoring/metrics');
      if (error) throw error as Error;
      // TODO: regenerate types — JVM/CPU/disk payload is ad-hoc on the
      // backend side and lands here as `Record<string, never>`.
      return data as never as MetricsResponse;
    }),
  );
}

export function useQueues(): UseQueryResult<QueueRow[], Error> {
  return useQuery<QueueRow[], Error>(
    buildOptions(monitoringKeys.queues(), async () => {
      const { data, error } = await api.GET('/api/v1/admin/monitoring/queues');
      if (error) throw error as Error;
      return (data ?? []) as QueueRow[];
    }),
  );
}

export function useRequestsLog(limit = 50): UseQueryResult<RequestLogEntry[], Error> {
  return useQuery<RequestLogEntry[], Error>(
    buildOptions(monitoringKeys.requests(limit), async () => {
      const { data, error } = await api.GET(
        '/api/v1/admin/monitoring/requests/log',
        { params: { query: { limit } } },
      );
      if (error) throw error as Error;
      // TODO: regenerate types — list rows are typed as `Record<string,
      // never>` while runtime shape includes timestamp/method/etc.
      return (data ?? []) as never as RequestLogEntry[];
    }),
  );
}

export function useRequestLogDetail(
  id: number | null,
): UseQueryResult<RequestLogDetail, Error> {
  return useQuery<RequestLogDetail, Error>({
    queryKey: monitoringKeys.requestDetail(id ?? -1),
    enabled: id !== null,
    queryFn: async () => {
      if (id === null) {
        throw new Error('Missing request log id');
      }
      const { data, error } = await api.GET(
        '/api/v1/admin/monitoring/requests/log/{id}',
        { params: { path: { id } } },
      );
      if (error) throw error as Error;
      // TODO: regenerate types — detail payload lacks an explicit schema.
      return data as never as RequestLogDetail;
    },
  });
}
